import { useRouter } from "expo-router";
import { useState } from "react";
import { Linking, Platform } from "react-native";

import { trpc } from "../trpc";

interface UseManageSubscriptionReturn {
  busy: boolean;
  error: string | null;
  /**
   * Send the user wherever they manage their subscription:
   * Stripe-hosted portal (web), App Store subscriptions (iOS IAP), or
   * Play subscriptions (Android IAP). Spec: "One-click cancel from
   * in-app + customer portal."
   */
  openPortal: () => Promise<void>;
}

// Apple and Google both publish a deep link that drops the user into
// "your subscriptions" — works whether they're using our app or any
// other. These are stable, documented URLs.
const APPLE_SUBS_URL = "itms-apps://apps.apple.com/account/subscriptions";
const GOOGLE_SUBS_URL = "https://play.google.com/store/account/subscriptions";

export function useManageSubscription(): UseManageSubscriptionReturn {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function openPortal(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      const res = await trpc.billing.openPortal.mutate();
      if (res.kind === "apple-iap") {
        await Linking.openURL(APPLE_SUBS_URL);
        return;
      }
      if (res.kind === "google-iap") {
        await Linking.openURL(GOOGLE_SUBS_URL);
        return;
      }
      // stripe-portal
      if (Platform.OS === "web") {
        window.location.assign(res.url);
      } else {
        router.push(res.url);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "couldn't open portal");
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, openPortal };
}
